import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import Layout from "../layout/Layout";
import { IconButton, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { SectionName, setTabNameLayout } from "../../redux/slices/layout";

import {
  setAllSilos,
  setComesFromMap,
  setCrop,
  setFilterModel,
  setShowFilterForm,
  setPreselectedFilterField,
  setVisibleRangeDashboardPicker,
} from "../../redux/slices/dashboard/dashboard";

import DynamicChartCountActions from "./charts/dynamicCountChart";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";

import NewActivityChart from "./charts/NewActivityChart";

import { DashboardTable } from "./charts/DashboardTable";
import { PocketLabTable } from "./charts/PocketLabTable";
import PocketLabChart from "./charts/PocketLabChart";
import {
  getPocketLabRecentVersion,
  pocketLabDevicesChart,
} from "../../redux/actions/dashboard/dashboardAsyncActions";
import { ThunkDispatch } from "@reduxjs/toolkit";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import MenuCrops from "./crops/CropsMenu";
import {
  fetchCerealSlugs,
  fetchCropInfo,
} from "../../redux/actions/dashboard/dynamicCharts";

import DataDateRangePicker from "../dataTable/table/MenuOptions/options/datePicker/dataDateRangePicker";
import {
  fetchUserActivity,
  activityChartValues,
} from "../../redux/actions/dashboard/dashboardDataAsync.actions";
import {
  setDataDateActivity,
  setDateRangePickerOpen,
  setSelectAllSitesFilter,
  setSelectSites,
} from "../../redux/slices/dashboard/dashboardFilters";
import { fetchDashboardLocationsFilter } from "../../redux/actions/dashboard/dashboardFiltersAsyncActions";

import { ValuesSelected } from "../../redux/slices/dataTable/dataTableFilters";

import { DashboardSelectors } from "../../redux/selectors/unifiedSelectors";
import { dashboardStyles } from "./DashboarsStyles";
import { CustomFilterButton } from "../customFilterComponent/customFilterButton";
import useDashboardFilters from "./dashboardPanel/DashboardFilters";
import { statusColumnOptions } from "../dataTable/table/customFilterForm/StatusColumnHelper";
import CountActivity from "./DashboardCount";
import { Man } from "@mui/icons-material";
import ManageWheatBarleyTitleCount from "./DashboardDynamicCount";

const SetActions = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  return {
    setOpenCloseDataRangePicker: (openClose: boolean) => {
      dispatch(setVisibleRangeDashboardPicker(openClose));
    },

    fetchActivityAll: (token: string, comesFromMap: boolean) => {
      if (!comesFromMap) {
        dispatch(getPocketLabRecentVersion(token));
        dispatch(setAllSilos(true));
        dispatch(fetchDashboardLocationsFilter());
        setTimeout(() => {
          dispatch(fetchUserActivity());
          dispatch(activityChartValues());
          dispatch(pocketLabDevicesChart({ token }));
        }, 500);
      }

      if (comesFromMap) {
        setTimeout(() => {
          dispatch(setComesFromMap(false));
        }, 1000);
      }
    },
    fetchCropsSlugs: () => {
      dispatch(fetchCerealSlugs());
    },

    fetchCropInfo: (cropSlug: string) => {
      dispatch(fetchCropInfo(cropSlug));
    },

    setCrop: (crop: { slug: string; translation: string }) => {
      dispatch(setCrop(crop));
    },

    setTabName: (name: SectionName) => dispatch(setTabNameLayout(name)),

    //Dashboard 2.0

    openRangeDate: () => dispatch(setDateRangePickerOpen(true)),
    closeRangeDate: () => dispatch(setDateRangePickerOpen(false)),

    setDataDateActivity: (date: Date[]) => {
      dispatch(setDataDateActivity(date));
    },

    setSelectAllSitesFilter: (valuesSelected: ValuesSelected[]) => {
      dispatch(setSelectAllSitesFilter(valuesSelected));
    },

    setSelectSites: (valuesSelected: ValuesSelected[]) => {
      dispatch(setSelectSites(valuesSelected));
    },

    /// custom filter component
    setFilterModel: (filterModel: any) => {
      dispatch(setFilterModel(filterModel));
    },

    setShowFilterForm: (show: boolean) => {
      dispatch(setShowFilterForm(show));
    },

    setPreselectedFilterField: (field: string | null) => {
      dispatch(setPreselectedFilterField(field));
    },
  };
};

export interface InfoTitle {
  title: string;
  count: number;
  fontColor: string;
  icon: boolean;
  type: string;
}

const Dashboard: any = () => {
  const [t] = useTranslation("global");
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const {
    setTabName,
    fetchActivityAll,
    fetchCropsSlugs,
    fetchCropInfo,
    setCrop,

    //Dashboard 2.0
    openRangeDate,
    closeRangeDate,
    setDataDateActivity,

    setSelectAllSitesFilter,

    // custom filter component
    setFilterModel,
    setShowFilterForm,
    setPreselectedFilterField,
  } = SetActions();

  const {
    locale,
    allLocations,
    locationsSelected,
    countOfUsers,
    pocketlabVersion,
    comesFromMap,
    cropsSelected,
    selectedCrop,
    dynamicCharts,
    activityChart,
    allDataResults,
    hasDataInCharts,
    dataDateActivity,
    dateRangePickerOpen,
    dashboardFilterValues,
    locations,
    token,
    filterModel,
    showFilterForm,
    preselectedFilterField,
    loadingProgress,
    loadFilterForFirstTime,
    loading,
  } = useSelector(DashboardSelectors);

  const [value, setValue] = React.useState(t("Dashboard.activity"));
  const { filter } = useDashboardFilters();
  const filters = filter();

  const options = statusColumnOptions(t);
  const chartValuesColors = {
    blue: "#0288D1",
    green: "#2E7D32",
    grey: "#A3A3A3",
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  useEffect(() => {
    setTabName("/dashboard");
  }, []);

  const dashboardOptions = [
    t("Dashboard.activity"),
    t("DashboardDynamicCharts.crops"),
    t("Cereals.pocketLab"),
  ];

  const information = [
    {
      title: t("Dashboard.activity_user_count.total_users"),
      count: countOfUsers.totalUsers,
      fontColor: chartValuesColors.blue,
      icon: false,
      type: "total",
    },
    {
      title: t("Dashboard.activity_user_count.active_users"),
      count: countOfUsers.countOfUsersActive,
      fontColor: chartValuesColors.green,
      icon: true,
      type: "active",
    },
    {
      title: t("Dashboard.activity_user_count.inactive_users"),
      count: countOfUsers.countOfUsersInactive,
      fontColor: chartValuesColors.grey,
      icon: true,
      type: "inactive",
    },
  ];

  const filterInformation = {
    typeOfAutocomplete: "locations",
    dataOptions: dashboardFilterValues.locations,
    allValuesSelected: locations.allValuesSelected,
    valuesSelected: locations.valuesSelected,
  };

  const Tooltips = (tooltipText: string) => {
    return (
      <Tooltip title={t(tooltipText)} placement="right">
        <IconButton sx={dashboardStyles.activityChartIcon}>
          <InfoOutlinedIcon />
        </IconButton>
      </Tooltip>
    );
  };

  const ActivityChartTitle = () => {
    return (
      <div className="activity-chart-title-container">
        <h5>{t("Dashboard.activity_user_count.total_actions_per_specie")}</h5>
        {Tooltips("Dashboard.actions_desc")}
      </div>
    );
  };

  const activitySection = () => (
    <div>
      <CountActivity countObject={information} t={t} />
      <div className="container-charts-with-result-activity">
        <div className="activity-chart-container">
          {ActivityChartTitle()}
          {NewActivityChart(t)}
        </div>

        <div className="dashboard-table-container">
          <div className="title-dashboard-table">
            {t("Fleet_layout.Users_groups")}
          </div>
          {DashboardTable(t, locale)}
        </div>
      </div>
    </div>
  );

  const cropsSection = () => (
    <div>
      <MenuCrops
        cropsSelected={cropsSelected}
        selectedCrop={selectedCrop}
        setCrop={setCrop}
        fetchCropInfo={fetchCropInfo}
        t={t}
        allDataResults={allDataResults.data}
      />
      <Box sx={dashboardStyles.outboundInboundMessageContainer}>
        <Tooltip
          title={
            <>
              <div>
                {t("DashboardDynamicCharts.outbound")}:{" "}
                {t("DashboardDynamicCharts.outboundTooltip")}
              </div>
              <div style={dashboardStyles.tooltipContainer}>
                {t("DashboardDynamicCharts.inbound")}:{" "}
                {t("DashboardDynamicCharts.inboundTooltip")}
              </div>
            </>
          }
          placement="bottom"
        >
          <Box sx={dashboardStyles.tooltipContentWrapper}>
            <div>{t("DashboardDynamicCharts.outboundInboundMessage")}</div>
            <HelpOutlineIcon sx={dashboardStyles.helpIcon} />
          </Box>
        </Tooltip>
      </Box>
      <div>
        <ManageWheatBarleyTitleCount
          title={
            selectedCrop.slug === "" ? (
              t("Create_multi_location.step_three.results")
            ) : (
              <>
                {t("Create_multi_location.step_three.results")} (
                {selectedCrop.translation}){" "}
                {Tooltips("DashboardDynamicCharts.actions_exclu")}
              </>
            )
          }
          count={dynamicCharts?.sumActions || 0}
          selectedCrop={selectedCrop}
          loading={loading.dynamicCharts}
        />
      </div>

      <div className="container-charts-with-result">
        <DynamicChartCountActions
          dynamicChart={
            dynamicCharts?.createArrayForCharts?.length > 0
              ? dynamicCharts?.createArrayForCharts
              : [{ variety: "", actions: 0 }]
          }
          avgProtWheat={
            dynamicCharts?.CreateArrayAverageForAvgChart?.length > 0
              ? dynamicCharts?.CreateArrayAverageForAvgChart
              : [{ variety: "", average: 0 }]
          }
          t={t}
          loading={loading.dynamicCharts}
          selectedCrop={selectedCrop}
        />
      </div>
    </div>
  );

  const pocketLabSection = () => {
    return (
      <div>
        <div className="container-last-version">
          <div className="version-pocketlab-title-tab">
            {t("Dashboard.version")}
          </div>
          {pocketlabVersion ? (
            <div className="version-container-number">
              <div>{t("Dashboard.last_version")} :</div>
              <div className="chip-version-dashboard">{pocketlabVersion}</div>
            </div>
          ) : (
            ""
          )}
        </div>
        <div className="pocketlab-container-section">
          {PocketLabTable(t, locale)}
          <div>{PocketLabChart(t)}</div>
        </div>
      </div>
    );
  };

  const fetchActivityChartValues = () => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const transform = filterInformation.dataOptions?.map((c) => c);

    if (
      filterInformation.valuesSelected.length === 0 &&
      !filterInformation.allValuesSelected
    ) {
      setSelectAllSitesFilter(transform);
    }
    dispatch(fetchUserActivity());
    dispatch(activityChartValues());
    dispatch(pocketLabDevicesChart({ token }));
  };

  useEffect(() => {
    fetchActivityAll(token, comesFromMap);
    fetchCropsSlugs();
  }, []);

  const globalCharts = () => (
    <div className="background-dashboard">
      <div className="input-date-picker-dashboard-container">
        <div className="mr-3">
          <DataDateRangePicker
            setActivityDate={setDataDateActivity}
            selectDataDateActivity={dataDateActivity}
            openRangeDate={openRangeDate}
            closeRangeDate={closeRangeDate}
            open={dateRangePickerOpen}
            callTheFetchData={fetchActivityChartValues}
            loading={activityChart.loading}
            //optional

            noData={!hasDataInCharts.hasData}
            hasDataBeenLoaded2={hasDataInCharts.hasDataBeenLoaded}
          />
        </div>

        <CustomFilterButton
          t={t}
          isDisabled={false}
          loadingsValues={activityChart.loading || loadingProgress.isLoading}
          filterModel={filterModel}
          setShowFilterForm={setShowFilterForm}
          showFilterForm={showFilterForm}
          setPreselectedFilterField={setPreselectedFilterField}
          preselectedField={preselectedFilterField}
          columnsTable={filters}
          setFilterModel={setFilterModel}
          handleApplyFilters={fetchActivityChartValues}
          hasNoData={!hasDataInCharts.hasData}
          comesFromDashboard={true}
          loadForFirstTime={loadFilterForFirstTime}
          statusOptions={options}
        />
      </div>
      <div className="container-tabs" data-cy="dashboard">
        <Box sx={dashboardStyles.tabContextContainer}>
          <TabContext value={value}>
            <Box sx={dashboardStyles.tabListContainer}>
              <TabList
                onChange={handleChange}
                variant="scrollable"
                scrollButtons="auto"
              >
                {dashboardOptions.map((options) => (
                  <Tab
                    sx={dashboardStyles.tab}
                    key={options}
                    label={options}
                    value={options}
                  />
                ))}
              </TabList>
            </Box>
            {dashboardOptions.map((options, key) => {
              if (options === t("Dashboard.activity")) {
                return (
                  <TabPanel
                    sx={dashboardStyles.tabPanel}
                    key={key}
                    value={options}
                  >
                    {activitySection()}
                  </TabPanel>
                );
              }

              if (options === t("DashboardDynamicCharts.crops")) {
                return (
                  <TabPanel
                    sx={dashboardStyles.tabPanel}
                    key={key}
                    value={options}
                  >
                    {cropsSection()}
                  </TabPanel>
                );
              }

              if (options === t("Cereals.pocketLab")) {
                return (
                  <TabPanel
                    sx={dashboardStyles.pocketLabTabPanel}
                    key={key}
                    value={options}
                  >
                    {pocketLabSection()}
                  </TabPanel>
                );
              }

              return "";
            })}
          </TabContext>
        </Box>
      </div>

      <div className="rest-of-component-dashboard" />
    </div>
  );

  return (
    <Layout title={t("Fleet_layout.Dashboard")} component={globalCharts()} />
  );
};

export default Dashboard;
